import { cva, type VariantProps } from 'class-variance-authority'
import { ChevronRight } from 'lucide-react'
import type { ReactNode } from 'react'

import { cn } from '@/frontend/lib/utils'

/** Same size scale as `Badge`, so the two line up when they sit in the same row. */
const expandBadgeVariants = cva(
  'inline-flex w-fit shrink-0 cursor-pointer items-center justify-center gap-1 rounded-full border font-medium whitespace-nowrap transition-colors [&>svg]:pointer-events-none [&>svg]:shrink-0',
  {
    variants: {
      size: {
        default: 'h-6 px-2.5 py-1 text-sm [&>svg:not([class*="size-"])]:size-3.5',
        lg: 'h-7 px-3 py-1.5 text-base [&>svg:not([class*="size-"])]:size-4',
        sm: 'h-5 px-2 py-0.5 text-xs [&>svg:not([class*="size-"])]:size-3',
        xs: 'h-4.5 px-1.5 py-0 text-2xs [&>svg:not([class*="size-"])]:size-2.5',
      },
      open: {
        true: 'border-foreground/20 bg-muted text-foreground',
        false: 'border-border text-muted-foreground hover:bg-muted hover:text-muted-foreground',
      },
    },
    defaultVariants: {
      size: 'xs',
      open: false,
    },
  }
)

interface ExpandBadgeProps extends VariantProps<typeof expandBadgeVariants> {
  open: boolean
  onToggle: () => void
  icon: ReactNode
  children: ReactNode
}

/** Badge-styled toggle button with a rotating chevron. */
export function ExpandBadge({ open, onToggle, icon, size, children }: ExpandBadgeProps) {
  return (
    <button type="button" onClick={onToggle} className={expandBadgeVariants({ size, open })}>
      {icon}
      {children}
      <ChevronRight className={cn('transition-transform', open && 'rotate-90')} />
    </button>
  )
}
